import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, PackageCheck } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

// Branch confirms what actually arrived. Good + damaged may not exceed what was shipped;
// anything missing is recorded as short. Only the good units are added to branch stock.
export const ReceiveTransferDialog = ({ transfer, onClose, onDone }) => {
  const outstanding = useMemo(() => (transfer?.items || []).filter(i => !i.posted), [transfer]);
  const [rows, setRows] = useState(() => Object.fromEntries(
    outstanding.map(i => [i.product_id, {
      good: String(i.pending_receipt?.good ?? i.quantity),
      damaged: String(i.pending_receipt?.damaged ?? 0),
      note: i.pending_receipt?.note || '',
    }])));
  const [busy, setBusy] = useState(false);

  if (!transfer) return null;

  const set = (pid, field, value) => setRows(r => ({ ...r, [pid]: { ...r[pid], [field]: value } }));

  const lineState = i => {
    const r = rows[i.product_id] || {};
    const good = Number(r.good), damaged = Number(r.damaged);
    const valid = Number.isInteger(good) && good >= 0 && Number.isInteger(damaged) && damaged >= 0 && good + damaged <= i.quantity;
    return { good, damaged, short: Math.max(0, i.quantity - good - damaged), valid };
  };
  const anyInvalid = outstanding.some(i => !lineState(i).valid);
  const anyDiscrepancy = outstanding.some(i => { const s = lineState(i); return s.valid && (s.damaged > 0 || s.short > 0); });

  const submit = async () => {
    setBusy(true);
    try {
      const receipts = outstanding.map(i => {
        const r = rows[i.product_id] || {};
        return { product_id: i.product_id, good: Number(r.good), damaged: Number(r.damaged || 0), note: (r.note || '').slice(0, 500) };
      });
      const { data } = await api.post(`/stock-transfers/${transfer.transfer_id}/receive`, { receipts });
      if (data.status === 'partial') toast.warning('Received — some items still need destination product mapping');
      else if (data.has_discrepancy) toast.warning('Received with a shortfall — the owner can see what came up short');
      else toast.success('Transfer received — stock posted');
      onDone();
      onClose();
    } catch (e) {
      let detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not receive the transfer');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={v => !v && !busy && onClose()}>
      <DialogContent className="transfer-dialog max-w-xl" data-testid="receive-transfer-dialog">
        <DialogHeader>
          <DialogTitle>Receive &amp; check in</DialogTitle>
          <DialogDescription>Confirm how many of each item actually arrived. Mark anything damaged or short so the owner sees what really came.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
          {!outstanding.length ? <p className="text-sm text-muted-foreground" data-testid="receive-nothing">Nothing outstanding to receive.</p> : outstanding.map(i => {
            const s = lineState(i);
            return (
              <div key={i.product_id} className="border rounded-md p-3 space-y-2" data-testid={`receive-line-${i.product_id}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <p className="font-medium text-sm truncate">{i.product_name}</p>
                  <span className="text-xs text-muted-foreground shrink-0">{i.quantity} shipped</span>
                </div>
                {i.unresolved && <p className="text-xs text-amber-700" data-testid={`receive-unresolved-${i.product_id}`}>No matching product at this branch yet — map it first; it will be skipped for now.</p>}
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor={`receive-good-${i.product_id}`}>Arrived good</Label>
                    <Input id={`receive-good-${i.product_id}`} type="number" min="0" step="1" className="w-24" value={rows[i.product_id]?.good ?? ''}
                      onChange={e => set(i.product_id, 'good', e.target.value)} data-testid={`receive-good-${i.product_id}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor={`receive-damaged-${i.product_id}`}>Damaged</Label>
                    <Input id={`receive-damaged-${i.product_id}`} type="number" min="0" step="1" className="w-24" value={rows[i.product_id]?.damaged ?? ''}
                      onChange={e => set(i.product_id, 'damaged', e.target.value)} data-testid={`receive-damaged-${i.product_id}`} />
                  </div>
                  <div className="text-xs pb-2" data-testid={`receive-short-${i.product_id}`}>
                    {!s.valid ? <span className="text-red-700">Good + damaged can't exceed {i.quantity}</span>
                      : s.short > 0 ? <span className="text-orange-700">{s.short} short</span>
                      : s.damaged > 0 ? <span className="text-orange-700">matches shipped</span>
                      : <span className="text-emerald-700">all accounted for</span>}
                  </div>
                </div>
                {(s.damaged > 0 || s.short > 0) && s.valid && (
                  <Input placeholder="Note for the owner (optional)" maxLength={500} value={rows[i.product_id]?.note || ''}
                    onChange={e => set(i.product_id, 'note', e.target.value)} data-testid={`receive-note-${i.product_id}`} />
                )}
              </div>
            );
          })}
        </div>
        {anyDiscrepancy && <p className="text-xs text-orange-700" data-testid="receive-discrepancy-hint">Only the good units will be added to your stock. The shortfall is logged for the owner.</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="receive-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || anyInvalid || !outstanding.length} data-testid="receive-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PackageCheck className="h-4 w-4 mr-2" />}Confirm arrival
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
